// Home: concise, action-oriented — greeting, child status cards with
// Continue Learning, quick actions, alerts, achievements and Pip
// recommendations. Everything else lives one tap away in the other tabs.
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Calculator, Compass, Gift, Heart, LineChart,
  MessageCircleHeart, Play, Plus, Settings, Users, UsersRound } from "lucide-react";
import { Button } from "../../ui/button";
import { SoundReadyBanner } from "../SoundReadyBanner";
import { ReactionAlerts } from "../ReactionAlerts";
import { DeadlineNudgeCard } from "../DeadlineNudgeCard";
import { FamilyWins } from "../../opportunities/FamilyWins";
import { FamilyFeed } from "../FamilyFeed";
import { WhatsNewSplash } from "../WhatsNewSplash";
import { CheerBadge } from "../CheerBadge";
import { BridgeRecommendation } from "../BridgeRecommendation";

const SECTION_TILES = [
  { to: "/dashboard/children", label: "Children", desc: "Profiles, coaches & speech targets", icon: Users, key: "children" },
  { to: "/dashboard/progress", label: "Progress", desc: "Mastery, reports & sharing", icon: LineChart, key: "progress" },
  { to: "/dashboard/family", label: "Family", desc: "Members & cheers", icon: UsersRound, key: "family" },
  { to: "/dashboard/settings", label: "Settings", desc: "Consent, privacy & controls", icon: Settings, key: "settings" },
];

export const HomeSection = ({ user, children, wishlistCount }) => {
  const navigate = useNavigate();
  return (
    <div className="space-y-8" data-testid="dash-section-home">
      <SoundReadyBanner />
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-extrabold font-display"
            data-testid="dashboard-welcome-heading">
            Hi {user?.name?.split(" ")[0]}
          </h1>
          <p className="text-muted-foreground mt-1">
            Your family space — you decide what happens here.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button asChild variant="outline" className="rounded-full gap-2 h-11 px-5"
            data-testid="assistant-link">
            <Link to="/assistant">
              <MessageCircleHeart className="h-4 w-4" aria-hidden="true" /> Ask the assistant
            </Link>
          </Button>
          <Button asChild variant="outline" className="rounded-full gap-2 h-11 px-5"
            data-testid="math-tutor-link">
            <Link to="/math">
              <Calculator className="h-4 w-4" aria-hidden="true" /> Math Tutor
            </Link>
          </Button>
          <Button asChild variant="outline" className="rounded-full gap-2 h-11 px-5"
            data-testid="opportunities-link">
            <Link to="/opportunities">
              <Compass className="h-4 w-4" aria-hidden="true" /> Opportunities
            </Link>
          </Button>
          <Button asChild variant="outline" className="rounded-full gap-2 h-11 px-5 relative"
            data-testid="kits-catalog-link">
            <Link to="/kits">
              <Gift className="h-4 w-4" aria-hidden="true" /> Kits
              {wishlistCount > 0 && (
                <span className="inline-flex items-center gap-1 rounded-full bg-secondary/15 text-secondary text-xs font-bold px-2 py-0.5"
                  data-testid="wishlist-count-badge">
                  <Heart className="h-3 w-3 fill-current" aria-hidden="true" />
                  {wishlistCount}
                </span>
              )}
            </Link>
          </Button>
          <Button className="rounded-full gap-2 h-11 px-5"
            onClick={() => navigate("/onboarding")} data-testid="add-child-button">
            <Plus className="h-4 w-4" aria-hidden="true" /> Add a child
          </Button>
        </div>
      </div>

      {user?.trial_ends_at && user.trial_ends_at > new Date().toISOString() &&
        user?.subscription?.status !== "active" && !user?.complimentary?.active && (
          <div className="rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3 flex items-center justify-between gap-3 flex-wrap"
            data-testid="trial-banner">
            <p className="text-sm font-bold text-amber-800">
              Trial access:{" "}
              {Math.max(1, Math.ceil((new Date(user.trial_ends_at) - Date.now()) / 86400000))}{" "}
              day(s) left — every feature is unlocked.
            </p>
            <Button asChild size="sm" variant="outline"
              className="rounded-full h-8 text-xs border-amber-400 text-amber-800 hover:bg-amber-100">
              <Link to="/billing" data-testid="trial-upgrade-link">
                Keep everything — see plans
              </Link>
            </Button>
          </div>
        )}

      {user?.subscription?.status === "active" &&
        user?.subscription?.trial_ends_at &&
        user.subscription.trial_ends_at > new Date().toISOString() && (
          <div className="rounded-2xl border border-emerald-300 bg-emerald-50 px-4 py-3 flex items-center justify-between gap-3 flex-wrap"
            data-testid="stripe-trial-banner">
            <p className="text-sm font-bold text-emerald-800">
              Free trial:{" "}
              {Math.max(1, Math.ceil((new Date(user.subscription.trial_ends_at) - Date.now()) / 86400000))}{" "}
              day(s) left — your plan bills automatically when the trial ends unless you cancel.
            </p>
            <Button asChild size="sm" variant="outline"
              className="rounded-full h-8 text-xs border-emerald-400 text-emerald-800 hover:bg-emerald-100">
              <Link to="/billing" data-testid="stripe-trial-billing-link">
                Manage plan
              </Link>
            </Button>
          </div>
        )}

      {user?.family_premium === false && (
        <div className="rounded-2xl border border-primary bg-primary/5 px-4 py-3 flex items-center justify-between gap-3 flex-wrap"
          data-testid="subscription-required-banner">
          <p className="text-sm font-bold">
            An active plan is required for learner access — choose one to start learning.
          </p>
          <Button asChild size="sm"
            className="rounded-full h-8 text-xs">
            <Link to="/billing" data-testid="subscribe-banner-link">
              See plans
            </Link>
          </Button>
        </div>
      )}

      {/* Continue Learning: one card per child, straight into their space */}
      {children === null ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : children.length === 0 ? (
        <div className="bg-card rounded-2xl border border-dashed border-border p-12 text-center space-y-4"
          data-testid="dashboard-empty-state">
          <h2 className="text-base md:text-lg font-bold font-display">No child profiles yet</h2>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            Add your first child to set up their consent-protected learning
            space. It takes about two minutes.
          </p>
          <Button className="rounded-full gap-2" onClick={() => navigate("/onboarding")}
            data-testid="empty-add-child-button">
            <Plus className="h-4 w-4" aria-hidden="true" /> Set up a child profile
          </Button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="home-children-row">
          {children.map((child) => (
            <div key={child.child_id}
              className="bg-card rounded-2xl border border-border p-5 space-y-3"
              data-testid={`home-child-card-${child.child_id}`}>
              <div className="flex items-center gap-3">
                <span className="h-11 w-11 rounded-full bg-violet-600 text-white flex items-center justify-center text-lg font-extrabold shrink-0">
                  {(child.display_name || "?").slice(0, 1).toUpperCase()}
                </span>
                <div className="min-w-0">
                  <p className="font-extrabold font-display truncate">{child.display_name}</p>
                  <p className="text-xs text-muted-foreground">Ages {child.age_band}</p>
                </div>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button size="sm" className="rounded-full gap-1.5 flex-1"
                  onClick={() => navigate(`/child/${child.child_id}`)}
                  data-testid={`continue-learning-${child.child_id}`}>
                  <Play className="h-3.5 w-3.5" aria-hidden="true" /> Continue Learning
                </Button>
                <Button size="sm" variant="outline" className="rounded-full"
                  onClick={() => navigate("/dashboard/children")}
                  data-testid={`manage-child-${child.child_id}`}>
                  Manage
                </Button>
                <Button size="sm" variant="ghost" className="rounded-full text-xs"
                  onClick={() => navigate("/dashboard/progress")}
                  data-testid={`view-progress-${child.child_id}`}>
                  View Progress <ArrowRight className="h-3 w-3 ml-1" aria-hidden="true" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Alerts, achievements & Pip recommendations */}
      <div className="space-y-4">
        <DeadlineNudgeCard />
        <ReactionAlerts />
        <FamilyWins />
        <CheerBadge />
        <BridgeRecommendation />
        <FamilyFeed />
        <WhatsNewSplash />
      </div>

      {/* Explore the other sections */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="home-section-tiles">
        {SECTION_TILES.map(({ to, label, desc, icon: Icon, key }) => (
          <Link key={key} to={to}
            className="bg-card rounded-2xl border border-border p-5 hover:border-violet-400 transition-colors group"
            data-testid={`home-tile-${key}`}>
            <Icon className="h-5 w-5 text-violet-600 mb-2" aria-hidden="true" />
            <p className="font-extrabold font-display flex items-center gap-1.5">
              {label}
              <ArrowRight className="h-3.5 w-3.5 opacity-0 group-hover:opacity-100 transition-opacity" aria-hidden="true" />
            </p>
            <p className="text-xs text-muted-foreground mt-1">{desc}</p>
          </Link>
        ))}
      </div>
    </div>
  );
};
